// ─────────────────────────────────────────────────────────────────────────────
// StrokeVisualizer.js — Cartesian force-vs-angle plot for one oarlock stroke.
//
// Renders:
//   • Force curve (N) over oar angle (deg) across the drive phase
//   • Theoretical CA/FA brackets (dashed) — derived from arccos(SP / OL)
//   • Actual CA/FA markers (solid)
//   • Slip wedge (red) — between theoretical_CA and actual_CA
//   • Wash wedge (red) — between actual_FA and theoretical_FA
//   • MXF peak dot + numeric labels
//
// Data source: listens to `crewz-gate-stroke` window events with full per-stroke
// arrays. The BLE drivers will emit these once GATT UUIDs are confirmed.
//
// Event shape:
//   window.dispatchEvent(new CustomEvent('crewz-gate-stroke', {
//     detail: {
//       side: 'port' | 'starboard' | 'sweep' | 'single_scull',
//       forceN: number[],     // zero-corrected
//       angleDeg: number[],   // zero-corrected
//       rigging: { ol, ib, sp, mfw2, ... },   // optional — falls back to localStorage
//       strokeIndex: number,
//       timestamp?: number,
//     }
//   }));
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ChevronDown, ChevronUp, Play } from 'lucide-react';
import { processStroke } from '../services/oarlock/mergedOarlockData';
import { loadRig } from '../services/oarlock/riggingStorage';
import { mockStroke, mockRigging } from '../services/oarlock/__fixtures__/section-11-7';

const SIDE_LABELS = {
  port: 'Bowside',
  starboard: 'Strokeside',
  sweep: 'Sweep',
  single_scull: 'Single Scull',
};

const SIDE_ACCENT = {
  port:        { stroke: '#dc2626', label: 'text-red-600 dark:text-red-400'      }, // red
  starboard:   { stroke: '#16a34a', label: 'text-emerald-600 dark:text-emerald-400' }, // green
  sweep:       { stroke: '#2563eb', label: 'text-blue-600 dark:text-blue-400'    }, // blue
  single_scull:{ stroke: '#7c3aed', label: 'text-violet-600 dark:text-violet-400'}, // violet
};

// SVG viewport / margins
const VB_W = 480;
const VB_H = 240;
const M = { t: 18, r: 16, b: 32, l: 44 };
const PLOT_W = VB_W - M.l - M.r;
const PLOT_H = VB_H - M.t - M.b;

function buildScales({ angleMin, angleMax, forceMax }) {
  const xRange = angleMax - angleMin || 1;
  const yMax = Math.max(forceMax, 50);
  const x = (a) => M.l + ((a - angleMin) / xRange) * PLOT_W;
  const y = (f) => M.t + PLOT_H - (f / yMax) * PLOT_H;
  return { x, y, yMax };
}

function StrokePlot({ stroke, metrics }) {
  const accent = SIDE_ACCENT[stroke.side] || SIDE_ACCENT.sweep;

  const { angleMin, angleMax, forceMax } = useMemo(() => {
    const angles = stroke.angleDeg;
    const forces = stroke.forceN;
    const tCA = metrics?.theoretical_CA;
    const tFA = metrics?.theoretical_FA;
    let aMin = Math.min(...angles);
    let aMax = Math.max(...angles);
    if (typeof tCA === 'number') aMin = Math.min(aMin, tCA);
    if (typeof tFA === 'number') aMax = Math.max(aMax, tFA);
    const fMax = Math.max(...forces);
    // Pad axes
    return {
      angleMin: aMin - 5,
      angleMax: aMax + 5,
      forceMax: fMax * 1.1,
    };
  }, [stroke, metrics]);

  const { x, y, yMax } = buildScales({ angleMin, angleMax, forceMax });

  // Force-angle polyline
  const points = stroke.angleDeg.map((a, i) => `${x(a).toFixed(1)},${y(stroke.forceN[i]).toFixed(1)}`).join(' ');

  // Slip / Wash wedges
  const tCA = metrics?.theoretical_CA;
  const tFA = metrics?.theoretical_FA;
  const aCA = metrics?.CA;
  const aFA = metrics?.FA;
  const slipBox = (typeof tCA === 'number' && typeof aCA === 'number' && tCA < aCA)
    ? { x1: x(tCA), x2: x(aCA) } : null;
  const washBox = (typeof aFA === 'number' && typeof tFA === 'number' && aFA < tFA)
    ? { x1: x(aFA), x2: x(tFA) } : null;

  // Y-axis ticks (0, mid, max)
  const yTicks = [0, yMax * 0.5, yMax].map(v => Math.round(v));
  // X-axis ticks: catch / 0 / finish region
  const xTicks = [angleMin, 0, angleMax].map(v => Math.round(v));

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      className="select-none"
      data-testid="stroke-visualizer-svg"
    >
      {/* Plot bg */}
      <rect x={M.l} y={M.t} width={PLOT_W} height={PLOT_H}
            className="fill-zinc-50 dark:fill-zinc-900/50" />

      {/* Slip wedge */}
      {slipBox && (
        <rect x={slipBox.x1} y={M.t} width={Math.max(1, slipBox.x2 - slipBox.x1)} height={PLOT_H}
              className="fill-rose-500/20" data-testid="stroke-visualizer-slip-wedge" />
      )}
      {/* Wash wedge */}
      {washBox && (
        <rect x={washBox.x1} y={M.t} width={Math.max(1, washBox.x2 - washBox.x1)} height={PLOT_H}
              className="fill-rose-500/20" data-testid="stroke-visualizer-wash-wedge" />
      )}

      {/* Y grid */}
      {yTicks.map((v) => (
        <g key={`yg-${v}`}>
          <line x1={M.l} x2={M.l + PLOT_W} y1={y(v)} y2={y(v)}
                className="stroke-zinc-200 dark:stroke-zinc-700" strokeWidth="0.5" />
          <text x={M.l - 6} y={y(v) + 3} textAnchor="end"
                className="fill-zinc-500 dark:fill-zinc-400" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
            {v}
          </text>
        </g>
      ))}
      <text x={M.l - 32} y={M.t + PLOT_H / 2} transform={`rotate(-90 ${M.l - 32} ${M.t + PLOT_H / 2})`}
            textAnchor="middle" className="fill-zinc-500 dark:fill-zinc-400"
            style={{ fontSize: 10, fontFamily: 'Archivo, sans-serif', fontWeight: 700, letterSpacing: '0.08em' }}>
        FORCE (N)
      </text>

      {/* X axis baseline */}
      <line x1={M.l} x2={M.l + PLOT_W} y1={M.t + PLOT_H} y2={M.t + PLOT_H}
            className="stroke-zinc-300 dark:stroke-zinc-600" strokeWidth="1" />
      {xTicks.map((v) => (
        <g key={`xg-${v}`}>
          <line x1={x(v)} x2={x(v)} y1={M.t + PLOT_H} y2={M.t + PLOT_H + 4}
                className="stroke-zinc-300 dark:stroke-zinc-600" strokeWidth="0.75" />
          <text x={x(v)} y={M.t + PLOT_H + 16} textAnchor="middle"
                className="fill-zinc-500 dark:fill-zinc-400" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
            {v}°
          </text>
        </g>
      ))}

      {/* Theoretical CA / FA dashed lines */}
      {typeof tCA === 'number' && (
        <g>
          <line x1={x(tCA)} x2={x(tCA)} y1={M.t} y2={M.t + PLOT_H}
                stroke="#9ca3af" strokeWidth="1" strokeDasharray="4 3" data-testid="stroke-visualizer-theoretical-ca" />
          <text x={x(tCA)} y={M.t - 4} textAnchor="middle"
                className="fill-zinc-500 dark:fill-zinc-400" style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace' }}>
            t.CA
          </text>
        </g>
      )}
      {typeof tFA === 'number' && (
        <g>
          <line x1={x(tFA)} x2={x(tFA)} y1={M.t} y2={M.t + PLOT_H}
                stroke="#9ca3af" strokeWidth="1" strokeDasharray="4 3" data-testid="stroke-visualizer-theoretical-fa" />
          <text x={x(tFA)} y={M.t - 4} textAnchor="middle"
                className="fill-zinc-500 dark:fill-zinc-400" style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace' }}>
            t.FA
          </text>
        </g>
      )}

      {/* Actual CA / FA solid markers */}
      {typeof aCA === 'number' && (
        <g>
          <line x1={x(aCA)} x2={x(aCA)} y1={M.t} y2={M.t + PLOT_H}
                stroke={accent.stroke} strokeWidth="1.25" data-testid="stroke-visualizer-actual-ca" />
          <text x={x(aCA)} y={M.t + 10} textAnchor="middle"
                fill={accent.stroke} style={{ fontSize: 10, fontFamily: 'Archivo, sans-serif', fontWeight: 700 }}>
            CA {Math.round(aCA)}°
          </text>
        </g>
      )}
      {typeof aFA === 'number' && (
        <g>
          <line x1={x(aFA)} x2={x(aFA)} y1={M.t} y2={M.t + PLOT_H}
                stroke={accent.stroke} strokeWidth="1.25" data-testid="stroke-visualizer-actual-fa" />
          <text x={x(aFA)} y={M.t + 10} textAnchor="middle"
                fill={accent.stroke} style={{ fontSize: 10, fontFamily: 'Archivo, sans-serif', fontWeight: 700 }}>
            FA {Math.round(aFA)}°
          </text>
        </g>
      )}

      {/* Force curve */}
      <polyline
        points={points}
        fill="none"
        stroke={accent.stroke}
        strokeWidth="2.25"
        strokeLinejoin="round"
        strokeLinecap="round"
        data-testid="stroke-visualizer-curve"
      />

      {/* MXF marker */}
      {typeof metrics?.MFA === 'number' && typeof metrics?.MXF === 'number' && (
        <g>
          <circle cx={x(metrics.MFA)} cy={y(metrics.MXF / (metrics.mfw2_applied ? 2 : 1))}
                  r="4" fill={accent.stroke} stroke="white" strokeWidth="1.5"
                  data-testid="stroke-visualizer-mxf-dot" />
          <text x={x(metrics.MFA)} y={y(metrics.MXF / (metrics.mfw2_applied ? 2 : 1)) - 8} textAnchor="middle"
                fill={accent.stroke} style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
            MXF {Math.round(metrics.MXF)} N
          </text>
        </g>
      )}
    </svg>
  );
}

function MetricsRow({ metrics }) {
  const cell = (label, value, unit) => (
    <div className="flex flex-col items-start" data-testid={`stroke-metric-${label.toLowerCase()}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</span>
      <span className="text-sm font-mono font-bold text-zinc-900 dark:text-white">
        {value == null ? '—' : value}
        {value != null && unit && <span className="text-zinc-500 dark:text-zinc-400 font-normal ml-0.5">{unit}</span>}
      </span>
    </div>
  );
  const fmt = (v, d = 0) => (typeof v === 'number' ? v.toFixed(d) : null);
  return (
    <div className="grid grid-cols-4 sm:grid-cols-7 gap-3 px-1">
      {cell('CA',  fmt(metrics?.CA, 0),  '°')}
      {cell('FA',  fmt(metrics?.FA, 0),  '°')}
      {cell('EL',  fmt(metrics?.EL, 0),  '°')}
      {cell('MXF', fmt(metrics?.MXF, 0), 'N')}
      {cell('OPW', fmt(metrics?.OPW, 0), 'W')}
      {cell('SL',  fmt(metrics?.SL, 1),  '°')}
      {cell('WA',  fmt(metrics?.WA, 1),  '°')}
    </div>
  );
}

export default function StrokeVisualizer({ defaultBoatId = 'BOAT1', defaultSeatNumber = 1, hardwareConnected = false } = {}) {
  const [stroke, setStroke] = useState(null);   // { side, forceN, angleDeg, rigging?, strokeIndex }
  const [expanded, setExpanded] = useState(false);

  // Resolve rigging: prefer event-payload rigging, else localStorage.
  const rigging = useMemo(() => {
    if (stroke?.rigging) return stroke.rigging;
    return loadRig(defaultBoatId, defaultSeatNumber) || null;
  }, [stroke, defaultBoatId, defaultSeatNumber]);

  // Run all algorithms + MFW2 boundary on the latest stroke.
  const metrics = useMemo(() => {
    if (!stroke?.forceN || !stroke?.angleDeg) return null;
    return processStroke({
      forceN: stroke.forceN,
      angleDeg: stroke.angleDeg,
      spm: stroke.spm,
      rigging: rigging || {},
      side: stroke.side || 'sweep',
    });
  }, [stroke, rigging]);

  // Listen for real per-stroke events from the BLE drivers (forward-compat).
  useEffect(() => {
    const onStroke = (event) => {
      const d = event?.detail;
      if (!d || !Array.isArray(d.forceN) || !Array.isArray(d.angleDeg)) return;
      setStroke(d);
      setExpanded(true);
    };
    window.addEventListener('crewz-gate-stroke', onStroke);
    return () => window.removeEventListener('crewz-gate-stroke', onStroke);
  }, []);

  const playDemoStroke = useCallback(() => {
    setStroke({
      side: 'sweep',
      forceN: [...mockStroke.forceN],
      angleDeg: [...mockStroke.angleDeg],
      rigging: { ...mockRigging },
      spm: 24,
      strokeIndex: 0,
      timestamp: Date.now(),
      _demo: true,
    });
    setExpanded(true);
  }, []);

  const sideLabel = stroke ? (SIDE_LABELS[stroke.side] || stroke.side) : null;
  const accent = stroke ? (SIDE_ACCENT[stroke.side] || SIDE_ACCENT.sweep) : SIDE_ACCENT.sweep;

  // Only render the card once smart-gate hardware is paired/streaming OR a stroke
  // has already been captured (so demo strokes and replays still render).
  if (!hardwareConnected && !stroke) return null;

  return (
    <div
      className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm"
      data-testid="stroke-visualizer-card"
    >
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full px-3 py-2 flex items-center gap-2 text-left"
        data-testid="stroke-visualizer-toggle"
      >
        <div className="w-6 h-6 rounded-md bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center shrink-0">
          <Activity className={`w-3 h-3 ${accent.label}`} />
        </div>
        <span className="text-xs font-bold uppercase tracking-wider text-zinc-900 dark:text-white whitespace-nowrap" style={{ fontFamily: 'Archivo, sans-serif' }}>
          Stroke Visualizer
        </span>
        <span className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate flex-1 min-w-0">
          {stroke
            ? <>· Last {sideLabel ? <span className={`font-bold ${accent.label}`}>{sideLabel}</span> : null}{stroke._demo && <span className="ml-1 px-1 py-px rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Demo</span>}</>
            : '· Awaiting smart-gate stream'}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3" data-testid="stroke-visualizer-body">
          {!stroke && (
            <div className="text-xs text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-900/40 rounded-lg p-3 border border-zinc-100 dark:border-zinc-700">
              No stroke captured yet. The visualizer will activate the moment your NK EmPower or
              XBoat smart gate streams a full stroke (BLE drivers pending UUID confirmation).
              Click <span className="font-bold">Demo</span> to preview the chart against the
              Section 11.7 reference stroke.
            </div>
          )}

          {stroke && metrics && (
            <>
              <StrokePlot stroke={stroke} metrics={metrics} />
              <MetricsRow metrics={metrics} />
              {(typeof metrics.SL === 'number' || typeof metrics.WA === 'number') && (
                <div className="text-[11px] text-zinc-500 dark:text-zinc-400 px-1">
                  <span className="font-bold text-zinc-900 dark:text-white uppercase tracking-wider">Read</span>
                  {' · '}
                  {typeof metrics.SL === 'number' && (
                    <span>SL {metrics.SL > 0 ? 'late catch' : 'reaches past theoretical'} ({metrics.SL.toFixed(1)}°)</span>
                  )}
                  {typeof metrics.SL === 'number' && typeof metrics.WA === 'number' && ' · '}
                  {typeof metrics.WA === 'number' && (
                    <span>WA {metrics.WA > 0 ? 'late finish' : 'extracts before theoretical'} ({metrics.WA.toFixed(1)}°)</span>
                  )}
                </div>
              )}
            </>
          )}

          <div className="flex items-center justify-end pt-1 border-t border-zinc-100 dark:border-zinc-700">
            <button
              type="button"
              onClick={playDemoStroke}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-[11px] font-bold uppercase tracking-wider hover:opacity-90 transition"
              data-testid="stroke-visualizer-demo-button"
            >
              <Play className="w-3 h-3" />
              Demo Stroke
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
